import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Chart } from "react-chartjs-2";
import "chart.js/auto";
import 'chartjs-adapter-date-fns';

const TransactionsChart = () => {
  const [labels, setLabels] = useState([]);
  const [totalTransactions, setTotalTransactions] = useState([]);
  const [completedTransactions, setCompletedTransactions] = useState([]);
  const [dailyProfits, setDailyProfits] = useState([]);
  const [volumeData, setVolumeData] = useState([]);

  const currentMonthData = useSelector(
    (state) => state.userReducer.transactionsChartData.currentMonth
  );

  useEffect(() => {
    if (currentMonthData && Object.keys(currentMonthData).length > 0) {
      const totalOrders = [];
      const completedOrders = [];
      const profits = [];
      const volumes = [];
      const chartLabels = [];

      Object.keys(currentMonthData).forEach((key) => {
        const dayData = currentMonthData[key];
        if (typeof dayData === "object" && dayData && dayData.day) {
          const dateLabel = new Date(dayData.year, dayData.month - 1, dayData.day); // Create a proper date object
          chartLabels.push(dateLabel);
          totalOrders.push(dayData.sumOrders || 0);
          completedOrders.push(dayData.completedOrders || 0);
          profits.push(dayData.eurProfit || 0);
          volumes.push(dayData.volume || 0);
        }
      });

      setTotalTransactions(totalOrders);
      setCompletedTransactions(completedOrders);
      setDailyProfits(profits);
      setVolumeData(volumes);
      setLabels(chartLabels);
    }
  }, [currentMonthData]);

  const MUTED = "#7c847e";
  const ACCENT = "#3ad17e";
  const GOLD = "#e6c06a";

  const data = {
    labels: labels,
    datasets: [
      {
        type: "bar",
        label: "Swaps",
        data: totalTransactions,
        backgroundColor: "rgba(58,209,126,.22)",
        borderColor: "rgba(58,209,126,.45)",
        borderWidth: 1,
        borderRadius: 6,
        maxBarThickness: 22,
        categoryPercentage: 0.6,
        barPercentage: 0.7,
        order: 3,
      },
      {
        type: "bar",
        label: "$ Volume",
        data: volumeData,
        yAxisID: "y3",
        backgroundColor: "rgba(120,140,255,.35)",
        borderRadius: 4,
        maxBarThickness: 10,
        categoryPercentage: 0.6,
        barPercentage: 0.5,
        order: 2,
      },
      {
        type: "line",
        label: "Completed",
        data: completedTransactions,
        yAxisID: "y1",
        borderColor: ACCENT,
        backgroundColor: (ctx) => {
          const chart = ctx.chart;
          const { ctx: c, chartArea } = chart;
          if (!chartArea) return "rgba(58,209,126,.15)";
          const g = c.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          g.addColorStop(0, "rgba(58,209,126,.35)");
          g.addColorStop(1, "rgba(58,209,126,0)");
          return g;
        },
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 4,
        order: 1,
      },
      {
        type: "line",
        label: "$ Profit",
        data: dailyProfits,
        yAxisID: "y2",
        borderColor: GOLD,
        backgroundColor: "transparent",
        borderWidth: 1.5,
        borderDash: [4, 3],
        pointRadius: 0,
        pointHoverRadius: 4,
        tension: 0.4,
        fill: false,
        order: 0,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    scales: {
      x: {
        type: "time",
        time: {
          unit: "day",
          displayFormats: { day: "dd" },
          tooltipFormat: "dd/MM/yyyy",
        },
        grid: { display: false },
        ticks: { color: MUTED, maxRotation: 0, autoSkipPadding: 12 },
        border: { color: "rgba(255,255,255,.08)" },
      },
      y: {
        beginAtZero: true,
        grid: { color: "rgba(255,255,255,.05)", drawBorder: false },
        ticks: { color: MUTED, precision: 0 },
      },
      y1: { display: false },
      y2: { display: false },
      y3: { display: false },
    },
    plugins: {
      legend: {
        display: true,
        position: "top",
        align: "end",
        labels: {
          color: "#a7b2ab",
          usePointStyle: true,
          pointStyle: "circle",
          boxWidth: 8,
          padding: 16,
        },
      },
      tooltip: {
        mode: "index",
        intersect: false,
        enabled: true,
        backgroundColor: "#151617",
        titleColor: "#ffffff",
        bodyColor: "#cdd6d0",
        titleFont: { size: 12, weight: "bold" },
        bodyFont: { size: 12 },
        borderColor: "rgba(255,255,255,.15)",
        borderWidth: 1,
        cornerRadius: 8,
        padding: 10,
        displayColors: true,
        usePointStyle: true,
        callbacks: {
          label: (item) => `  ${item.dataset.label}: ${item.formattedValue}`,
        },
      },
    },
  };

  return (
    <div className="w-full relative" style={{ height: "100%", minHeight: 300 }}>
      {data && options && <Chart type="bar" data={data} options={options} />}
    </div>
  );
};

export default TransactionsChart;
